/**
 * Respostas fixas do FAQ da página de contato (CollapseBox → components/
 * ContactPage/Faq*.vue).
 *
 * Moram aqui, e não dentro de cada componente, porque dois lugares leem o
 * mesmo texto: o componente, que desenha a resposta, e a tool WebMCP
 * `get_help_answer`, que devolve a resposta em texto corrido para o agente
 * (utils/faqRespostas.ts). Mudou o texto aqui, mudou nos dois.
 *
 * As chaves seguem a ordem do `labelFaq` (tradutors/contact.ts). Os três
 * tópicos que são formulário (senha, sugestão e contato) só têm `answer`: o
 * componente deles é o próprio formulário, e o texto existe para a tool.
 */

// `type`, e não `interface`: assim o objeto serve onde se espera Record<string, …>
// (utils/faqRespostas.ts).
type TextosFaq = {
  renew: { acesse: string; aqui: string };
  upgrade: {
    title: string;
    intro: string;
    linkText: string;
    afterLink: string;
    bonus: string;
    bonusValue: string;
    confirm: string;
    plansTitle: string;
    basicTitle: string;
    basicDesc: string;
    plusTitle: string;
    plusDesc: string;
    goldTitle: string;
    goldDesc: string;
  };
  upgradeBonus: {
    title: string;
    intro: string;
    account: string;
    linkText: string;
    bonus: string;
    bonusValue: string;
    confirm: string;
    final: string;
  };
  logout: { title: string; intro: string; linkText: string };
  statusAccount: { acesse: string; aqui: string };
  consultCredits: { title: string; intro: string; linkText: string };
  buyCredits: { title: string; intro: string; linkText: string };
  forgotPassword: { answer: string };
  photos: {
    title: string;
    text1: string;
    text2: string;
    text3: string;
    text4: string;
    link: string;
    textLink: string;
  };
  sugestion: { answer: string };
  cancel: { title: string; intro: string; linkText: string };
  contact: { answer: string };
};

const faq: { portugues: TextosFaq; ingles: TextosFaq } = {
  portugues: {
    renew: { acesse: "Acesse", aqui: "aqui" },
    upgrade: {
      title: "Como fazer upgrade no Bella da Semana?",
      intro:
        "Fazer upgrade no Bella da Semana é fácil como abrir aquela cerveja gelada depois do expediente. Basta acessar sua ",
      linkText: "Área de Assinante (CLIQUE AQUI)",
      afterLink: " e escolher o plano desejado.",
      bonus:
        "Você já visualiza automaticamente seu saldo de bônus para Upgrade, calculado com base nos dias que ainda não usou do seu plano atual.",
      bonusValue:
        "O valor do bônus é referente aos dias que você ainda tem no plano antigo. Então, quanto antes você decidir migrar, mais bônus terá para usar!",
      confirm:
        "Escolha seu novo plano e confirme o Upgrade. O início da assinatura nova começa na hora, sem enrolação.",
      plansTitle: "Planos disponíveis",
      basicTitle: "Plano Basic",
      basicDesc:
        "Se você está no Basic, bora dar esse passo: é qualidade de verdade, conteúdo de responsa!",
      plusTitle: "Plano Plus",
      plusDesc:
        "Tenha acesso a fotos em HD, vídeos em 4K, assistir ao vivo as lives, ver o making of, além de créditos para download. Este é o seu próximo passo para mais conteúdo de alta qualidade!",
      goldTitle: "Plano Gold",
      goldDesc:
        "A experiência VIP definitiva! Desfrute de todos os benefícios do Bella da Semana com o diferencial dos créditos ilimitados para download. Baixe quantos vídeos e making of quiser, sem restrições e sem se preocupar com cobranças extras!",
    },
    upgradeBonus: {
      title: "Bônus de Upgrade no Bella da Semana",
      intro:
        "Chegou a hora de turbinar sua experiência no Bella da Semana com aquele bônus de respeito que só quem é assinante recebe. Aqui o negócio é direto: bônus não é enfeite, é oportunidade para subir de patamar e curtir ainda mais!",
      account: 'Faça o login na sua conta, depois clique em "Minha Conta" ou',
      linkText: "CLIQUE AQUI",
      bonus:
        "Lá você irá visualizar automaticamente seu saldo de bônus para Upgrade, calculado com base nos dias que ainda não usou do seu plano atual.",
      bonusValue:
        "O valor do bônus é referente aos dias que você ainda tem no plano antigo. Então, quanto antes você decidir migrar, mais bônus terá para usar!",
      confirm:
        "Escolha seu novo plano e confirme o Upgrade! O início da assinatura nova começa na hora, sem enrolação.",
      final:
        "Agora não tem desculpa: aproveite seu bônus, faça o upgrade e viva a experiência completa no maior clube do Brasil.",
    },
    logout: {
      title:
        "Percebeu alguma instabilidade para ver as fotos, assistir aos vídeos ou gerenciar sua assinatura?",
      intro:
        "Na maioria das vezes, uma simples reinicialização da sua sessão resolve tudo.",
      linkText: "CLIQUE AQUI PARA REINICIAR SEU ACESSO",
    },
    statusAccount: { acesse: "Acesse", aqui: "aqui" },
    consultCredits: {
      title:
        "Quer saber exatamente quantos créditos você tem na conta para planejar seus próximos downloads?",
      intro: "Manter o controle é simples e direto.",
      linkText: "CLIQUE AQUI PARA CONSULTAR SEU SALDO DE CRÉDITOS",
    },
    buyCredits: {
      title:
        "Com os Créditos para Download você baixa os vídeos e making of em 4K garantindo acesso a qualquer momento, mesmo offline.",
      intro: "É simples e direto.",
      linkText:
        "CLIQUE AQUI PARA GARANTIR SEUS CRÉDITOS e tenha seu conteúdo sempre à mão.",
    },
    forgotPassword: {
      answer:
        "Para trocar a senha, peça o e-mail de redefinição: informe o e-mail da conta no formulário deste tópico, na página de contato, e confirme o envio.",
    },
    photos: {
      title: "Como resolver falhas de carregamento e login",
      text1:
        "Para tornar sua navegação mais ágil, os navegadores salvam informações temporárias chamadas de cache e cookies.",
      text2:
        "No entanto, com o tempo, esse acúmulo de dados pode gerar conflitos e causar erros inesperados no site.",
      text3:
        "Limpar esses arquivos é um procedimento padrão e seguro que remove dados antigos, permitindo que o site carregue a versão mais recente e estável para você.",
      text4: "Clique aqui para realizar uma limpeza automática:",
      link: "/limpar-cache",
      textLink: "Limpar Cache",
    },
    sugestion: {
      answer:
        "Para enviar suas críticas ou sugestões, preencha com seus dados o formulário deste tópico, na página de contato.",
    },
    cancel: {
      title:
        "Quer cancelar sua assinatura? O processo é rápido e totalmente online. Após a confirmação, seu acesso permanece ativo até o final do período que você já pagou.",
      intro: "",
      linkText: "CLIQUE AQUI PARA ENTRAR EM CONTATO E CANCELAR SUA ASSINATURA.",
    },
    contact: {
      answer:
        "Para entrar em contato, preencha o formulário deste tópico, na página de contato.",
    },
  },

  ingles: {
    renew: { acesse: "Access", aqui: "here" },
    upgrade: {
      title: "How to upgrade at Bella Club?",
      intro:
        "Upgrading to Bella Club is as easy as cracking open a cold beer after work. Just access your Subscriber Area (",
      linkText: "CLICK HERE",
      afterLink: ") and choose the plan you want.",
      bonus:
        "You will automatically see your Upgrade bonus balance, calculated based on the days you have not yet used on your current plan.",
      bonusValue:
        "The bonus value is based on the days you have remaining on your old plan. So, the sooner you decide to switch, the more bonuses you'll have to use!",
      confirm:
        "Choose your new plan and confirm the upgrade. Your new subscription starts immediately, no hassle.",
      plansTitle: "Available plans",
      basicTitle: "Basic Plan",
      basicDesc:
        "If you're on Basic, let's take this step: it's real quality, responsible content!",
      plusTitle: "Plus Plan",
      plusDesc:
        "Get access to HD photos, 4K videos, live streaming, making-of footage, and download credits. This is your next step toward more high-quality content!",
      goldTitle: "Gold Plan",
      goldDesc:
        "The ultimate VIP experience! Enjoy all the benefits of Bella Club with the added bonus of unlimited download credits. Download as many videos and making-of footage as you want, with no restrictions and no extra charges!",
    },
    upgradeBonus: {
      title: "Upgrade Bonus at Bella Club",
      intro:
        "It's time to boost your Bella Club experience with that incredible bonus that only subscribers receive. Here's the deal: bonuses aren't just frills; they're opportunities to level up and enjoy even more!",
      account: 'Log in to your account then click on "My Account" or click',
      linkText: "HERE",
      bonus:
        "There you will automatically see your Upgrade bonus balance, calculated based on the days you have not yet used on your current plan.",
      bonusValue:
        "The bonus value is based on the days you have remaining on your old plan. So, the sooner you decide to switch, the more bonuses you'll have to use!",
      confirm:
        "Choose your new plan and confirm your upgrade! Your new subscription starts immediately, no hassle.",
      final:
        "Now there's no excuse: take advantage of your bonus, upgrade, and enjoy the full experience of Brazil's biggest men's club.",
    },
    logout: {
      title:
        "Having trouble viewing photos, watching videos, or managing your subscription?",
      intro:
        "Most of the time, simply restarting your session solves everything.",
      linkText: "CLICK HERE TO RESTART YOUR ACCESS",
    },
    statusAccount: { acesse: "Access", aqui: "here" },
    consultCredits: {
      title:
        "Want to know exactly how many credits you have to plan your next downloads?",
      intro: "Keeping track is simple and straightforward.",
      linkText: "CLICK HERE TO CHECK YOUR CREDIT BALANCE",
    },
    buyCredits: {
      title:
        "With Download Credits you can download videos and making-of in 4K, ensuring access anytime, even offline.",
      intro: "It's simple and straightforward.",
      linkText:
        "CLICK HERE TO GET YOUR CREDITS and always have your content at hand.",
    },
    forgotPassword: {
      answer:
        "To change your password, request the reset e-mail: enter your account e-mail in this topic's form, on the contact page, and confirm sending it.",
    },
    photos: {
      title: "How to fix loading and login issues",
      text1:
        "To keep your browsing sharp and fast, browsers save temporary information called cache and cookies.",
      text2:
        "Over time, however, this data buildup can create conflicts and cause unexpected errors on the site.",
      text3:
        "Clearing these files is a standard, secure procedure that removes outdated data, allowing Bella Club to load the most recent and stable version for you.",
      text4: "Click here to perform an automatic cleanup:",
      link: "/limpar-cache",
      textLink: "Clear Cache",
    },
    sugestion: {
      answer:
        "To send your criticisms or suggestions, fill out this topic's form, on the contact page, with your details.",
    },
    cancel: {
      title:
        "Want to cancel your subscription? The process is quick and fully online. After confirmation, your access remains active until the end of the period you have already paid for.",
      intro: "",
      linkText: "CLICK HERE TO CONTACT US AND CANCEL YOUR SUBSCRIPTION.",
    },
    contact: {
      answer: "To contact us, fill out this topic's form, on the contact page.",
    },
  },
};

export type { TextosFaq };
export default faq;
